// src/pages/TextTools/QRCodeGenerator.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateQRCode } from '../../services/chatApi';
import { Download, QrCode, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const QRCodeGenerator = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [qrData, setQrData] = useState('');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleGenerate = async () => {
        setError(null);
        setGeneratedImage(null);

        if (!qrData.trim()) {
            setError(t('qrcode.enterDataFirst'));
            return;
        }

        setIsLoading(true);

        try {
            const result = await generateQRCode(qrData);
            
            // Direct response with image data
            setGeneratedImage(`data:image/png;base64,${result.image_data}`);
            
            // Update credits
            if (result.new_credits !== undefined) {
                console.log(`Credits updated. New balance: ${result.new_credits}`);
                setCredits(result.new_credits);
            }
            
            setIsLoading(false);
        } catch (err) {
            setError(err.message || t('qrcode.errorGeneric'));
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!generatedImage) return;

        const link = document.createElement('a');
        link.href = generatedImage;
        link.download = 'qrcode.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClear = () => {
        setQrData('');
        setGeneratedImage(null);
        setError(null);
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
           {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
            <SubscribePrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('qrcode.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('qrcode.description')}
                    </p>

                    {/* QR Data Input */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium mb-2">
                            {t('qrcode.dataLabel')}
                        </label>
                        <textarea
                            value={qrData}
                            onChange={(e) => setQrData(e.target.value)}
                            placeholder={t('qrcode.dataPlaceholder')}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[120px] resize-y rounded-md"
                            disabled={isLoading}
                        />
                         </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('qrcode.generatingButton')}
                                </>
                            ) : (
                                <>
                                    <QrCode className="mr-2" size={20} />
                                    {t('qrcode.generateButton')}
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            disabled={isLoading}
                        >
                            {t('qrcode.clearButton')}
                        </button>
                    </div>

                    {/* Display Generated QR Code */}
                    {generatedImage && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold">
                                    {t('qrcode.generatedQRCode')}
                                </h4>
                            </div>
                            
                            <div className="relative border border-slate-300 rounded-md overflow-hidden bg-white p-8 flex justify-center">
                                <img 
                                    src={generatedImage} 
                                    alt="Generated QR Code" 
                                    className="max-w-sm w-full h-auto"
                                />
                            </div>

                            {/* Download Button */}
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('qrcode.downloadPNG')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QRCodeGenerator;
